import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  Tooltip
} from '@mui/material';

// Иконки
import ResizeIcon from '@mui/icons-material/OpenWith';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';

function TableTools({ 
  table, 
  position, 
  onClose, 
  onTableUpdate 
}) {
  const [isResizeMode, setIsResizeMode] = useState(false);
  const [columnWidths, setColumnWidths] = useState([]);
  const dragCleanupRef = useRef(null);

  // Инициализация размеров при первом открытии
  React.useEffect(() => {
    setIsResizeMode(false);
    setColumnWidths([]);
    if (table) {
      // Получаем текущие ширины колонок
      const firstRow = table.querySelector('tr');
      if (firstRow) {
        const cells = firstRow.querySelectorAll('td, th');
        const widths = Array.from(cells).map(cell => {
          const width = cell.style.width || cell.offsetWidth + 'px';
          return parseInt(width);
        });
        setColumnWidths(widths);
      }
    }
  }, [table]);

  // Включение/выключение режима ресайзеров
  const toggleResizeMode = useCallback(() => {
    const newMode = !isResizeMode;
    setIsResizeMode(newMode);
    
    if (newMode) {
      addResizers();
    } else {
      removeResizers();
    }
  }, [isResizeMode, table]);

  // Добавляем ресайзеры к таблице
  const addResizers = useCallback(() => {
    if (!table) return;

    // Удаляем старые ресайзеры
    removeResizers();
    
    const firstRow = table.querySelector('tr');
    if (!firstRow) return;

    const cells = firstRow.querySelectorAll('td, th');
    
    cells.forEach((cell, columnIndex) => {
      if (columnIndex < cells.length - 1) { // Не добавляем к последней колонке
        const resizer = document.createElement('div');
        resizer.className = 'table-column-resizer';
        resizer.dataset.columnIndex = columnIndex.toString();
        
        // Стили для ресайзера
        Object.assign(resizer.style, {
          position: 'absolute',
          top: '0',
          right: '-3px',
          width: '6px',
          height: '100%',
          cursor: 'col-resize',
          backgroundColor: '#2196F3',
          zIndex: '1000',
          opacity: '0.7',
          userSelect: 'none'
        });
        
        // Добавляем в ячейку
        cell.style.position = 'relative';
        cell.appendChild(resizer);
        
        // Обработчики для ресайзера
        setupResizerHandlers(resizer, columnIndex);
      }
    });

    // Выделяем таблицу в режиме ресайза
    table.style.border = '3px solid #2196F3';
    table.style.boxShadow = '0 0 10px rgba(33, 150, 243, 0.3)';
  }, [table]);

  // Убираем ресайзеры
  const removeResizers = useCallback(() => {
    dragCleanupRef.current?.();
    dragCleanupRef.current = null;
    if (!table) return;

    table.querySelectorAll('.table-column-resizer').forEach(resizer => {
      resizer.remove();
    });

    // Убираем выделение
    table.style.border = '2px solid #000';
    table.style.boxShadow = '';
  }, [table]);

  // Настройка обработчиков для ресайзера
  const setupResizerHandlers = useCallback((resizer, columnIndex) => {
    let isResizing = false;
    let startX = 0;
    let currentColumnWidth = 0;
    let nextColumnWidth = 0;
    let currentColumn = null;
    let nextColumn = null;

    const handleMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      isResizing = true;
      startX = e.clientX;
      
      // Получаем колонки
      const firstRow = table.querySelector('tr');
      const cells = firstRow.querySelectorAll('td, th');
      currentColumn = cells[columnIndex];
      nextColumn = cells[columnIndex + 1];
      
      if (currentColumn && nextColumn) {
        currentColumnWidth = currentColumn.offsetWidth;
        nextColumnWidth = nextColumn.offsetWidth;
        
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        
        document.addEventListener('mousemove', handleMouseMove, true);
        document.addEventListener('mouseup', handleMouseUp, true);
        dragCleanupRef.current = () => {
          isResizing = false;
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
          document.removeEventListener('mousemove', handleMouseMove, true);
          document.removeEventListener('mouseup', handleMouseUp, true);
        };
      }
    };

    const handleMouseMove = (e) => {
      if (!isResizing || !currentColumn || !nextColumn) return;
      
      e.preventDefault();
      
      const deltaX = Math.max(50 - currentColumnWidth, Math.min(nextColumnWidth - 50, e.clientX - startX));
      const newCurrentWidth = currentColumnWidth + deltaX;
      const newNextWidth = nextColumnWidth - deltaX;
      
      if (newCurrentWidth >= 50 && newNextWidth >= 50) {
        // Применяем ширину ко всем ячейкам в соответствующих столбцах
        const allRows = table.querySelectorAll('tr');
        allRows.forEach(row => {
          const rowCells = row.querySelectorAll('td, th');
          if (rowCells[columnIndex]) {
            rowCells[columnIndex].style.width = newCurrentWidth + 'px';
            rowCells[columnIndex].style.maxWidth = newCurrentWidth + 'px';
            rowCells[columnIndex].style.minWidth = newCurrentWidth + 'px';
          }
          if (rowCells[columnIndex + 1]) {
            rowCells[columnIndex + 1].style.width = newNextWidth + 'px';
            rowCells[columnIndex + 1].style.maxWidth = newNextWidth + 'px';
            rowCells[columnIndex + 1].style.minWidth = newNextWidth + 'px';
          }
        });
      }
    };

    const handleMouseUp = (e) => {
      if (isResizing) {
        e.preventDefault();
        
        isResizing = false;
        
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        
        document.removeEventListener('mousemove', handleMouseMove, true);
        document.removeEventListener('mouseup', handleMouseUp, true);
        dragCleanupRef.current = null;
        
        // Обновляем состояние ширин
        updateColumnWidths();
        
        // Уведомляем об изменении
        if (onTableUpdate) {
          onTableUpdate();
        }
      }
    };

    resizer.addEventListener('mousedown', handleMouseDown);
  }, [table, onTableUpdate]);

  // Обновляем массив ширин колонок
  const updateColumnWidths = useCallback(() => {
    if (!table) return;
    
    const firstRow = table.querySelector('tr');
    if (firstRow) {
      const cells = firstRow.querySelectorAll('td, th');
      const widths = Array.from(cells).map(cell => parseInt(cell.style.width) || cell.offsetWidth);
      setColumnWidths(widths);
    }
  }, [table]);

  // Сброс к исходным размерам
  const resetSizes = useCallback(() => {
    if (!table) return;

    const allRows = table.querySelectorAll('tr');
    const firstRowCells = table.querySelector('tr')?.querySelectorAll('td, th');
    
    if (firstRowCells) {
      // Равномерное распределение ширины
      const totalWidth = 750; // Максимальная ширина для A4
      const cellWidth = Math.floor(totalWidth / firstRowCells.length);
      
      allRows.forEach(row => {
        const cells = row.querySelectorAll('td, th');
        cells.forEach(cell => {
          cell.style.width = cellWidth + 'px';
          cell.style.maxWidth = cellWidth + 'px';
          cell.style.minWidth = cellWidth + 'px';
          cell.style.height = 'auto'; // Высота автоматическая
        });
      });
    }

    table.style.tableLayout = 'fixed';
    updateColumnWidths();
    
    if (onTableUpdate) {
      onTableUpdate();
    }
  }, [table, onTableUpdate, updateColumnWidths]);

  // Очистка при закрытии
  React.useEffect(() => {
    return () => {
      if (table) {
        removeResizers();
      }
    };
  }, [table, removeResizers]);

  if (!table || !position) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        left: position.x + 20,
        top: position.y,
        zIndex: 1500,
        minWidth: 300
      }}
    >
      <Paper 
        elevation={8} 
        sx={{ 
          p: 2, 
          backgroundColor: 'white', 
          border: '2px solid #2196F3',
          borderRadius: 2
        }}
      >
        {/* Заголовок */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" color="primary">
            🔧 Инструменты таблицы
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Закрыть инструменты таблицы">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Основные кнопки */}
        <Stack spacing={2}>
          <Button
            variant={isResizeMode ? "contained" : "outlined"}
            startIcon={<ResizeIcon />}
            onClick={toggleResizeMode}
            fullWidth
          >
            {isResizeMode ? 'Завершить изменение размеров' : 'Изменить размеры колонок'}
          </Button>

          {isResizeMode && (
            <Box>
              <Typography variant="body2" color="primary" gutterBottom>
                🔄 Режим активен! Перетаскивайте синие линии между колонками для изменения ширины.
              </Typography>
              
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                💡 Высота строк автоматически подстраивается под содержимое
              </Typography>

              {/* Кнопка сброса */}
              <Button
                variant="outlined"
                startIcon={<RestoreIcon />}
                onClick={resetSizes}
                size="small"
                fullWidth
              >
                Сбросить к равномерным размерам
              </Button>
            </Box>
          )}

          <Divider />

          {/* Информация */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              💡 Нажмите "Изменить размеры" чтобы появились ресайзеры между колонками
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

export default TableTools;
